import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  RefreshCw,
  Mail,
  Eye,
  ArrowLeft,
  Edit3,
  Image,
  BookOpen,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/CuratorArtistDetail.css';

const CuratorArtistDetail = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};

  // State management
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });

  // Collection selector state
  const [collectionSelectorOpen, setCollectionSelectorOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [currentCollections, setCurrentCollections] = useState([]);
  const [collectionsViewOpen, setCollectionsViewOpen] = useState(false);
  const [assigningCollectionId, setAssigningCollectionId] = useState(null);
  const [collectionSelectorError, setCollectionSelectorError] = useState(null);
  const [accessibleCollections, setAccessibleCollections] = useState([]);

  // Get curator's accessible collections for the selected organization
  const getAccessibleCollections = useCallback(async () => {
    try {
      if (!selectedOrganization?.organization_id) {
        return { collections: [] };
      }

      const { data: collections, error: collectionsError } = await supabase
        .from('collections')
        .select('collection_id, title, description, organization_id')
        .eq('organization_id', selectedOrganization.organization_id);

      if (collectionsError) throw collectionsError;

      return { collections: collections || [] };
    } catch (error) {
      console.error('Error getting accessible collections:', error);
      return { collections: [] };
    }
  }, [selectedOrganization?.organization_id]);

  // Open collection selector modal
  const openCollectionSelector = async (artwork) => {
    setSelectedArtwork(artwork);
    setCollectionSelectorOpen(true);
    setCollectionSelectorError(null);
    setAssigningCollectionId(null);
    setCurrentCollections([]);
    
    try {
      const { data: collections, error } = await supabase
        .from('collection_vc_mapping')
        .select(`
          collection_id,
          collections!inner(title, description)
        `)
        .eq('vc_id', artwork.vc_id);

      if (error) {
        console.error('Error fetching collections for artwork:', error);
        setCurrentCollections([]);
        return;
      }
      
      const transformedCollections = collections
        .map(item => ({
          collection_id: item.collection_id,
          title: item.collections.title,
          description: item.collections.description
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
      
      setCurrentCollections(transformedCollections);
    } catch (error) {
      console.error('Error in openCollectionSelector:', error);
      setCurrentCollections([]);
      setCollectionSelectorError('Unable to load collections for this artwork.');
    }
  };

  const closeCollectionSelector = () => {
    setCollectionSelectorOpen(false);
    setSelectedArtwork(null);
    setCurrentCollections([]);
    setCollectionSelectorError(null);
    setAssigningCollectionId(null);
  };

  const handleAssignToCollection = async (collectionId) => {
    if (!collectionId || !selectedArtwork) return;

    const alreadyAssigned = currentCollections.some(
      (collection) => Number(collection.collection_id) === Number(collectionId)
    );

    if (alreadyAssigned) {
      closeCollectionSelector();
      return;
    }

    setAssigningCollectionId(collectionId);
    setCollectionSelectorError(null);

    try {
      const { error } = await supabase
        .from('collection_vc_mapping')
        .upsert(
          {
            collection_id: Number(collectionId),
            vc_id: selectedArtwork.vc_id,
            accepted: true,
            order_num: 100
          },
          { onConflict: 'vc_id,collection_id' }
        );

      if (error) throw error;

      closeCollectionSelector();
      loadArtistAndArtworks();
    } catch (assignError) {
      console.error('Error assigning artwork to collection:', assignError);
      setCollectionSelectorError('Could not add the artwork to that collection. Please try again.');
    } finally {
      setAssigningCollectionId(null);
    }
  };

  const openCollectionsView = async (artwork) => {
    setSelectedArtwork(artwork);
    
    try {
      const { data: collections, error } = await supabase
        .from('collection_vc_mapping')
        .select(`
          collection_id,
          collections!inner(title, description)
        `)
        .eq('vc_id', artwork.vc_id);

      if (error) {
        console.error('Error fetching collections:', error);
        setCurrentCollections([]);
      } else {
        const transformedCollections = collections
          .map(item => ({
            collection_id: item.collection_id,
            title: item.collections.title,
            description: item.collections.description
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
        
        setCurrentCollections(transformedCollections);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCurrentCollections([]);
    }
    
    setCollectionsViewOpen(true);
  };

  const closeCollectionsView = () => {
    setCollectionsViewOpen(false);
    setSelectedArtwork(null);
    setCurrentCollections([]);
  };

  // Load artist details and their artworks
  const loadArtistAndArtworks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get artist details
      const { data: artistData, error: artistError } = await supabase
        .from('users')
        .select('*')
        .eq('uuid', artistId)
        .single();

      if (artistError) throw artistError;
      setArtist(artistData);

      // Get all VCs (artworks) by this artist
      const { data: artworksData, error: artworksError } = await supabase
        .from('vcs')
        .select(`
          vc_id,
          uuid,
          title,
          description,
          price,
          width,
          height,
          depth,
          created_at,
          cover_image,
          access_control
        `)
        .eq('uuid', artistId)
        .eq('access_control', 'Public');

      if (artworksError) throw artworksError;

      if (!artworksData || artworksData.length === 0) {
        setArtworks([]);
        setTotalCount(0);
        setStats({
          total: 0,
          collections: 0,
          avgPrice: 0
        });
        setLoading(false);
        return;
      }

      const vcIds = artworksData.map(vc => vc.vc_id);

      // Get the collection mappings for these VCs
      const { data: mappings, error: mappingsError } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          collection_id,
          collections(
            collection_id,
            title,
            description
          )
        `)
        .in('vc_id', vcIds);

      if (mappingsError) throw mappingsError;

      // Build a map of collections per artwork
      const artworkCollectionsMap = new Map();
      const allCollectionIds = new Set();

      if (mappings) {
        mappings.forEach(mapping => {
          if (!artworkCollectionsMap.has(mapping.vc_id)) {
            artworkCollectionsMap.set(mapping.vc_id, []);
          }
          artworkCollectionsMap.get(mapping.vc_id).push({
            id: mapping.collection_id,
            title: mapping.collections?.title,
            description: mapping.collections?.description
          });
          allCollectionIds.add(mapping.collection_id);
        });
      }

      // Transform artworks with collection info
      const transformedArtworks = artworksData.map(artwork => ({
        ...artwork,
        collections: artworkCollectionsMap.get(artwork.vc_id) || [],
        dimensions: `${artwork.width || 0}" × ${artwork.height || 0}"${artwork.depth ? ` × ${artwork.depth}"` : ''}`,
        formattedPrice: artwork.price ? `$${parseFloat(artwork.price).toLocaleString()}` : 'Not for sale'
      }));

      setArtworks(transformedArtworks);
      setTotalCount(transformedArtworks.length);

      // Calculate stats
      const prices = transformedArtworks
        .map(a => a.price)
        .filter(price => price && price > 0)
        .map(price => parseFloat(price));
      const avgPrice = prices.length > 0 
        ? prices.reduce((sum, price) => sum + price, 0) / prices.length 
        : 0;

      setStats({
        total: transformedArtworks.length,
        collections: allCollectionIds.size,
        avgPrice: avgPrice
      });

      // Fetch accessible collections
      const { collections } = await getAccessibleCollections();
      setAccessibleCollections(collections);

    } catch (err) {
      console.error('Error loading artist details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [artistId, getAccessibleCollections]);

  useEffect(() => {
    if (artistId) {
      loadArtistAndArtworks();
    }
  }, [artistId, loadArtistAndArtworks]);

  // Filter and sort artworks
  const filteredArtworks = artworks
    .filter(artwork => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        (artwork.title || '').toLowerCase().includes(searchLower) ||
        (artwork.description && artwork.description.toLowerCase().includes(searchLower))
      );

      // Price filter
      let matchesPrice = true;
      if (priceFilter.min !== '' || priceFilter.max !== '') {
        const price = parseFloat(artwork.price) || 0;
        if (priceFilter.min !== '' && price < parseFloat(priceFilter.min)) {
          matchesPrice = false;
        }
        if (priceFilter.max !== '' && price > parseFloat(priceFilter.max)) {
          matchesPrice = false;
        }
      }

      return matchesSearch && matchesPrice;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'price':
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        case 'created_at':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  // Paginate results
  const from = (currentPage - 1) * pageSize;
  const totalPages = Math.ceil(filteredArtworks.length / pageSize);
  const paginatedArtworks = filteredArtworks.slice(from, from + pageSize);

  if (loading) {
    return (
      <div className="curator-artist-detail">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="curator-artist-detail">
        <div className="error-message">
          <p>Error loading artist: {error || 'Artist not found'}</p>
          <button onClick={() => navigate(-1)} className="retry-button">
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-artist-detail">
      {/* Back button */}
      <button 
        onClick={() => navigate(-1)} 
        className="back-button"
        title="Go back"
      >
        <ArrowLeft size={20} />
        Back to Artists
      </button>

      {/* Artist Header */}
      <div className="artist-detail-header">
        <div className="artist-profile">
          {artist.profile_img ? (
            <img src={artist.profile_img} alt={artist.nickname} className="artist-avatar" />
          ) : (
            <div className="artist-avatar-placeholder">
              {(artist.nickname || 'A').substring(0, 2).toUpperCase()}
            </div>
          )}
          
          <div className="artist-info">
            <h1 className="artist-name">{artist.nickname || `${artist.first_name} ${artist.last_name}`}</h1>
            {/* Show full name if nickname is used */}
            {artist.nickname && (artist.first_name || artist.last_name) && (
              <p className="artist-fullname" style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.25rem' }}>
                {artist.first_name} {artist.last_name}
              </p>
            )}
            {artist.handle && <p className="artist-handle">@{artist.handle}</p>}
            <div className="artist-contact-info" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
              <div className="artist-contact">
                <Mail size={14} />
                <a href={`mailto:${artist.email}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{artist.email}</a>
              </div>
              {artist.mobile_phone && (
                <div className="artist-contact" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '14px' }}>📱</span>
                  <a href={`tel:${artist.mobile_phone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>{artist.mobile_phone}</a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats in Header */}
        {stats && (
          <div className="header-stats">
            <div className="mini-stat">
              <div className="mini-stat-icon">
                <Image size={16} />
              </div>
              <div className="mini-stat-content">
                <span className="mini-stat-number">{stats.total}</span>
                <span className="mini-stat-label">Artworks</span>
              </div>
            </div>
            
            <div className="mini-stat">
              <div className="mini-stat-icon">
                <BookOpen size={16} />
              </div>
              <div className="mini-stat-content">
                <span className="mini-stat-number">{stats.collections}</span>
                <span className="mini-stat-label">Collections</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="artworks-filters">
        <div className="search-bar">
          <Search size={16} color="#6c757d" />
          <input
            type="text"
            placeholder="Search artworks..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchQuery && (
            <button 
              className="clear-search"
              onClick={() => setSearchQuery('')}
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <Filter size={16} color="#6c757d" />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created_at">Sort by Date</option>
            <option value="title">Sort by Title</option>
            <option value="price">Sort by Price</option>
          </select>
        </div>

        <button 
          className="filter-toggle"
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Filter size={16} color="#6c757d" />
          Filters
          {filtersOpen ? <ChevronUp size={14} color="#6c757d" /> : <ChevronDown size={14} color="#6c757d" />}
        </button>

        <div className="view-toggle">
          <button 
            onClick={() => setViewMode('grid')}
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            title="Grid view"
          >
            <Grid3X3 size={18} />
          </button>
          <button 
            onClick={() => setViewMode('list')}
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            title="List view"
          >
            <List size={18} />
          </button>
        </div>
        
        <button onClick={loadArtistAndArtworks} className="refresh-button">
          <RefreshCw size={16} color="#6c757d" />
          Refresh
        </button>
      </div>

      {/* Advanced Filters */}
      {filtersOpen && (
        <div className="advanced-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Min Price</label>
              <input
                type="number"
                placeholder="0"
                value={priceFilter.min}
                onChange={(e) => setPriceFilter({...priceFilter, min: e.target.value})}
              />
            </div>
            
            <div className="filter-group">
              <label>Max Price</label>
              <input
                type="number"
                placeholder="No limit"
                value={priceFilter.max}
                onChange={(e) => setPriceFilter({...priceFilter, max: e.target.value})}
              />
            </div>
            
            <div className="filter-group">
              <label>Order</label>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Artworks Content */}
      {paginatedArtworks.length === 0 ? (
        <div className="no-artworks">
          <Image size={48} color="#6c757d" />
          <h3>No artworks found</h3>
          <p>No artworks found for this artist.</p>
        </div>
      ) : (
        <>
          <div className={`artworks-container ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="artworks-masonry">
                {paginatedArtworks.map((artwork) => (
                  <div 
                    key={artwork.vc_id}
                    className="artwork-card-new"
                  >
                    {/* Image Container - masonry style */}
                    <div className="artwork-image-container-masonry" onClick={() => openCollectionsView(artwork)}>
                      {artwork.cover_image ? (
                        <img 
                          src={artwork.cover_image} 
                          alt={artwork.title}
                          className="artwork-image-natural"
                          onError={(e) => {
                            e.target.src = '/images/placeholder-artwork.jpg';
                          }}
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          <Image size={32} color="#cbd5e1" />
                          <span>No Image</span>
                        </div>
                      )}
                      
                      {/* Overlay on hover */}
                      <div className="artwork-overlay">
                        <div className="overlay-icon">
                          <Eye size={24} color="#ffffff" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Info Section */}
                    <div className="artwork-card-info">
                      <div className="artwork-header">
                        <h4 className="artwork-title">{artwork.title}</h4>
                        <p className="artwork-artist">{artist.nickname || artist.handle || 'Unknown'}</p>
                      </div>
                      
                      {/* Stats Row */}
                      <div className="artwork-stats-row">
                        {artwork.width && artwork.height && (
                          <span className="dimensions">{artwork.width}"×{artwork.height}"</span>
                        )}
                        {artwork.price > 0 && (
                          <span className="price">{artwork.formattedPrice}</span>
                        )}
                        <span className="collections-count">
                          {artwork.collections.length > 0 
                            ? `${artwork.collections.length} collection${artwork.collections.length > 1 ? 's' : ''}` 
                            : 'Unassigned'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons Row */}
                    <div className="artwork-actions-row">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/stories/create?artworkId=${artwork.vc_id}`);
                        }}
                        className="action-btn story-btn"
                        title="Create Art Speaks story"
                      >
                        <BookOpen size={14} />
                        Create Story
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openCollectionSelector(artwork);
                        }}
                        className="action-btn collection-btn"
                        title="Add to collection"
                      >
                        <BookOpen size={14} />
                        Add to Collection
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/curator/artworks/${artwork.vc_id}/edit`);
                        }}
                        className="action-btn edit-btn"
                        title="Edit artwork details"
                      >
                        <Edit3 size={14} />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="artwork-table">
                <div className="table-header">
                  <div className="header-cell">Image</div>
                  <div className="header-cell">Title</div>
                  <div className="header-cell">Dimensions</div>
                  <div className="header-cell">Price</div>
                  <div className="header-cell">Collections</div>
                  <div className="header-cell">Actions</div>
                </div>
                {paginatedArtworks.map((artwork) => (
                  <div key={artwork.vc_id} className="table-row">
                    <div className="table-cell image-cell">
                      {artwork.cover_image ? (
                        <img src={artwork.cover_image} alt={artwork.title} />
                      ) : (
                        <div className="no-image-small">No Image</div>
                      )}
                    </div>
                    <div className="table-cell title-cell">{artwork.title}</div>
                    <div className="table-cell">{artwork.dimensions}</div>
                    <div className="table-cell price-cell">{artwork.formattedPrice}</div>
                    <div className="table-cell">
                      {artwork.collections.length > 0 
                        ? artwork.collections.map(c => c.title).join(', ')
                        : 'Unassigned'}
                    </div>
                    <div className="table-cell actions-cell">
                      <button
                        onClick={() => openCollectionSelector(artwork)}
                        className="action-btn-small"
                        title="Add to collection"
                      >
                        <BookOpen size={14} />
                      </button>
                      <button
                        onClick={() => navigate(`/curator/artworks/${artwork.vc_id}/edit`)}
                        className="action-btn-small"
                        title="Edit"
                      >
                        <Edit3 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {from + 1}-{Math.min(from + pageSize, totalCount)} of {totalCount} artworks
              </div>
              
              <div className="pagination-controls">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button 
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
              
              <div className="page-size-selector">
                <label>Show:</label>
                <select 
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                  <option value={96}>96</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {/* Collection Selector Modal */}
      {collectionSelectorOpen && selectedArtwork && (
        <div className="modal-overlay" onClick={closeCollectionSelector}>
          <div className="collection-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Collection</h3>
              <button className="modal-close" onClick={closeCollectionSelector}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="selected-artwork-preview">
                {selectedArtwork.cover_image && (
                  <img src={selectedArtwork.cover_image} alt={selectedArtwork.title} />
                )}
                <div className="artwork-preview-info">
                  <h4>{selectedArtwork.title}</h4>
                  <p>{artist.nickname || artist.handle}</p>
                </div>
              </div>

              {currentCollections.length > 0 && (
                <div className="current-collections">
                  <h4>Currently in:</h4>
                  <div className="collection-badges">
                    {currentCollections.map(col => (
                      <span key={col.collection_id} className="collection-badge">
                        {col.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {collectionSelectorError && (
                <div className="modal-error">{collectionSelectorError}</div>
              )}

              <div className="available-collections">
                <h4>Add to collection:</h4>
                {accessibleCollections.length === 0 ? (
                  <p className="no-collections-msg">No collections available. Create a collection first.</p>
                ) : (
                  <div className="collections-list">
                    {accessibleCollections
                      .filter(col => !currentCollections.some(c => c.collection_id === col.collection_id))
                      .map(collection => (
                        <button
                          key={collection.collection_id}
                          className={`collection-option ${assigningCollectionId === collection.collection_id ? 'assigning' : ''}`}
                          onClick={() => handleAssignToCollection(collection.collection_id)}
                          disabled={assigningCollectionId !== null}
                        >
                          <BookOpen size={16} />
                          <span>{collection.title}</span>
                          {assigningCollectionId === collection.collection_id && (
                            <RefreshCw size={14} className="spinner" />
                          )}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collections View Modal */}
      {collectionsViewOpen && selectedArtwork && (
        <div className="modal-overlay" onClick={closeCollectionsView}>
          <div className="collections-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Artwork Details</h3>
              <button className="modal-close" onClick={closeCollectionsView}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-content">
              <div className="artwork-detail-view">
                {selectedArtwork.cover_image && (
                  <img src={selectedArtwork.cover_image} alt={selectedArtwork.title} className="detail-image" />
                )}
                <div className="detail-info">
                  <h2>{selectedArtwork.title}</h2>
                  <p className="detail-artist">{artist.nickname || artist.handle}</p>
                  {selectedArtwork.dimensions && (
                    <p className="detail-dimensions">{selectedArtwork.dimensions}</p>
                  )}
                  {selectedArtwork.price > 0 && (
                    <p className="detail-price">{selectedArtwork.formattedPrice}</p>
                  )}
                  {selectedArtwork.description && (
                    <p className="detail-description">{selectedArtwork.description}</p>
                  )}
                </div>
              </div>

              {currentCollections.length > 0 && (
                <div className="collections-section">
                  <h4>In Collections:</h4>
                  <div className="collection-badges">
                    {currentCollections.map(col => (
                      <span 
                        key={col.collection_id} 
                        className="collection-badge clickable"
                        onClick={() => navigate(`/curator/collections/${col.collection_id}`)}
                      >
                        {col.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  className="action-btn primary"
                  onClick={() => {
                    closeCollectionsView();
                    openCollectionSelector(selectedArtwork);
                  }}
                >
                  <BookOpen size={16} />
                  Add to Collection
                </button>
                <button 
                  className="action-btn secondary"
                  onClick={() => navigate(`/curator/artworks/${selectedArtwork.vc_id}/edit`)}
                >
                  <Edit3 size={16} />
                  Edit Artwork
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorArtistDetail;
